import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { isoLinesLayerConfig } from "@/constants/layerConfigAttributes";
import { FeatureHubGuard } from "@/featureFlags";
import type { LayerUnion } from "@/layers";
import { readIsoLayerCustomOptions } from "@/layers/geojson/isoLayerShared";
import { useDesktop } from "@/overlay/components";
import { IntegerInput } from "@/overlay/components/CommonParts/CustomInput";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import {
  CalibratedToggle,
  ColorPickerInput,
  OpacityInput,
} from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { WindowKind, instanceId } from "@/overlay/components/window";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import type { MapId } from "@/types/type-utils";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { transformEnsSelectStringToArray } from "@/utility/ensemble";
import { t } from "@lingui/core/macro";
import type { IsoLinesLayer } from "@mm/metx-workbench.meteomatics.com";
import { useRef, useState } from "react";
import { BufferedTextInput } from "./IsoBufferedTextInput";
import {
  type IsoInputField,
  asObject,
  buildIsoSelectionPatch,
  getInitialIsoInputField,
  normalizeIsoInput,
  toInputValue,
} from "./IsoLayerFormUtils";
import { IsoSelectionTypeSwitch } from "./IsoSelectionTypeSwitch";

export function IsoLinesLayerForm(props: {
  layer: IsoLinesLayer;
  mapId: MapId;
  changeProps: (propsValue: Partial<LayerUnion>) => void;
  onEditLayerParam: () => void;
}) {
  const { layer, mapId, changeProps, onEditLayerParam } = props;
  const desktop = useDesktop();
  const hideStyleInputs = transformEnsSelectStringToArray(layer.ens_select ?? "").length > 1;
  const customOptions = readIsoLayerCustomOptions(layer.custom_options);
  const [selectedField, setSelectedField] = useState<IsoInputField | null>(() =>
    getInitialIsoInputField({
      withIsobandIntervals: false,
      values: layer.values,
      valueRange: layer.value_range,
    }),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const focusSelectedInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const applySelectedField = (field: IsoInputField) => {
    setSelectedField(field);
    focusSelectedInput();
  };

  const getActiveSelectionValue = (field: IsoInputField | null) => {
    if (field === null) {
      return null;
    }
    if (field === "values") {
      return layer.values;
    }
    return layer.value_range;
  };

  const handleSelectField = (field: IsoInputField) => {
    if (selectedField !== null && field === selectedField) {
      focusSelectedInput();
      return;
    }

    if (!normalizeIsoInput(getActiveSelectionValue(selectedField) ?? "")) {
      applySelectedField(field);
      return;
    }

    desktop.dispatch({
      type: "confirmationWindow",
      windowId: instanceId(WindowKind.ConfirmationWindow),
      customProps: {
        message: t`Values will be reset. Confirming this action will reset the values of isolines.`,
        callback: (confirmed: boolean) => {
          if (!confirmed) {
            return;
          }

          changeProps(
            buildIsoSelectionPatch({
              field,
              rawValue: "",
              withIsobandIntervals: false,
            }),
          );
          applySelectedField(field);
        },
      },
    });
  };

  return (
    <>
      <ModelAndWeatherParameterForm layer={layer} mapId={mapId} onEditParameter={onEditLayerParam} />
      <OpacityInput
        layer={layer}
        change={changeProps}
        onStart={() => batchGroupBy.start()}
        onEnd={() => batchGroupBy.end()}
      />
      {hideStyleInputs || (
        <ColorPickerInput
          label={t`Text Color`}
          change={(color) => changeProps({ text_color: color })}
          value={layer.text_color}
        />
      )}
      <FormField label={t`Text Size`}>
        <IntegerInput
          {...isoLinesLayerConfig.textSizeLimits}
          onChange={(value) => changeProps({ text_size: value })}
          value={layer.text_size || 0}
        />
      </FormField>
      {hideStyleInputs || (
        <ColorPickerInput
          label={t`Line Color`}
          change={(color) => changeProps({ line_color: color })}
          value={layer.line_color}
        />
      )}
      <FormField label={t`Line Width`}>
        <IntegerInput
          {...isoLinesLayerConfig.lineWidthLimits}
          onChange={(value) => changeProps({ line_width: value })}
          value={layer.line_width || 0}
        />
      </FormField>
      <IsoSelectionTypeSwitch selectedField={selectedField} onSelect={handleSelectField} withIsobandIntervals={false} />
      {selectedField === null && (
        <FormField label={t`Selection`} noSemanticLinking={true}>
          <div style={{ fontSize: "0.9rem", color: "var(--color-gray-700)" }}>
            <span>{t`Select Values or Ranges to define isolines.`}</span>
          </div>
        </FormField>
      )}
      {selectedField === "values" && (
        <FormField label={<span>{t`Values`}</span>}>
          <BufferedTextInput
            ref={inputRef}
            type="text"
            placeholder={t`e.g. 0,0.5,1`}
            onCommit={(rawValue) =>
              changeProps(
                buildIsoSelectionPatch({
                  field: "values",
                  rawValue,
                  withIsobandIntervals: false,
                }),
              )
            }
            value={toInputValue(layer.values)}
          />
        </FormField>
      )}
      {selectedField === "value_range" && (
        <FormField label={<span>{t`Range`}</span>}>
          <BufferedTextInput
            ref={inputRef}
            type="text"
            placeholder={t`e.g. -40,40,5`}
            onCommit={(rawValue) =>
              changeProps(
                buildIsoSelectionPatch({
                  field: "value_range",
                  rawValue,
                  withIsobandIntervals: false,
                }),
              )
            }
            value={toInputValue(layer.value_range)}
          />
        </FormField>
      )}
      <FeatureHubGuard flag="feature.isobands_fixed_bbox">
        <FormField label={t`Fixed BBox`}>
          <BufferedTextInput
            type="text"
            placeholder={"LngMin,LatMin,LngMax,LatMax"}
            onCommit={(rawValue) =>
              changeProps({
                custom_options: {
                  ...asObject(layer.custom_options),
                  fixed_bbox: normalizeIsoInput(rawValue),
                },
              })
            }
            value={toInputValue(customOptions.fixed_bbox)}
          />
        </FormField>
      </FeatureHubGuard>
      <FeatureHubGuard flag="feature.isobands_resolution">
        <FormField label={t`Resolution`}>
          <BufferedTextInput
            type="text"
            placeholder={"0.05,0.05 or 400x500"}
            onCommit={(rawValue) =>
              changeProps({
                custom_options: {
                  ...asObject(layer.custom_options),
                  resolution: normalizeIsoInput(rawValue),
                },
              })
            }
            value={toInputValue(customOptions.resolution)}
          />
        </FormField>
      </FeatureHubGuard>
      <FeatureHubGuard flag="api_url_suffix">
        <InitDateField
          label={t`API URL suffix`}
          defaultValue={(customOptions as { api_url_suffix?: string }).api_url_suffix ?? ""}
          onChange={(apiUrlSuffix) =>
            changeProps({
              custom_options: {
                ...asObject(layer.custom_options),
                api_url_suffix: apiUrlSuffix,
              },
            })
          }
        />
      </FeatureHubGuard>

      <FormField label={t`Median filter`}>
        <IntegerInput
          {...isoLinesLayerConfig.gaussianAndMedianFilterLimits}
          onChange={(value) => changeProps({ filter_median: value })}
          value={layer.filter_median || 0}
        />
      </FormField>
      <FormField label={t`Gaussian filter`}>
        <IntegerInput
          {...isoLinesLayerConfig.gaussianAndMedianFilterLimits}
          onChange={(value) => changeProps({ filter_gauss: value })}
          value={layer.filter_gauss || 0}
        />
      </FormField>
      {hasCalibratedAvailable(layer.parameter_unit) && (
        <CalibratedToggle value={!!layer.calibrated} change={changeProps} />
      )}
    </>
  );
}
